/* eslint-disable indent */
import { CourseContent, Section, chapterBasicData, chapterDifficulty } from "interfaces/courses"
import Image from "next/dist/client/image"
import { FC } from "react"
import styles from "./CourseHeader.module.scss"
import Link from "next/dist/client/link"
import parse from "html-react-parser"
import CourseButton from "@cs/components/courses/course-button/CourseButton"
import InfoPills from "@cs/components/courses/info-pill/InfoPills"
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io"
import { useTranslation } from "next-i18next"
import { useProgress } from "@cs/lib/progress-v6/ProgressContext"
import { BiCheckCircle } from "react-icons/bi"
import { Skeleton } from "@cs/components/ui/loading-skeleton"
import { SignInDialog } from "@cs/components/signin-dialog"
import { Button } from "@cs/components/ui/button"
import { MixpanelService } from "@cs/services/mixpanel"

interface CourseHeaderProps extends CourseContent {
  difficulty: chapterDifficulty
  chaptersData: {
    simple: (chapterBasicData | undefined)[]
    advanced: (chapterBasicData | undefined)[]
  }
}

const CourseHeader: FC<CourseHeaderProps> = ({
  image,
  title,
  longDescription,
  timeToFinish,
  difficulty,
  chaptersData,
  slug,
  id,
  lastUpdated
}) => {
  const { t } = useTranslation("courses")
  const { progress, isLoading } = useProgress()
  const isStarted = !!progress.courses?.[id]?.startedAt
  const isCompleted = !!progress.courses?.[id]?.completedAt
  const chapters = difficulty === "advanced" ? chaptersData.advanced : chaptersData.simple
  const chapter = chapters[0]
  const section = chapter?.sections[0]

  const trackClick = (
    started: boolean,
    chapter: chapterBasicData | undefined,
    section: Section | undefined,
  ) => {
    if (!started) {
      MixpanelService.startCourse({
        title: title || "",
        courseId: id,
        version: "v6",
      })
    }

    MixpanelService.accessSection({
      title: title || "",
      chapterId: chapter?.id || "",
      courseId: id,
      sectionId: section?.id,
      version: "v6",
      started,
    })
  }

  return (
    <header className={styles.header}>
      <figure className={styles.background}>
        <Image src={image} alt={title} layout="responsive" width={20} height={10} />
      </figure>

      <section className={styles.content}>
        <CourseButton underline className={styles.backButton}>
          <IoIosArrowBack />
          <Link href="/new-courses">{t("back-to-courses")}</Link>
        </CourseButton>
        <h1 className={styles.h1}>{title}</h1>
        <p className={styles.p}>{parse(longDescription || "")}</p>
        {isCompleted ? (
          <span className={styles.completedPill}>
            {t("completed")}
            <BiCheckCircle />
          </span>
        ) : (
          <InfoPills className={styles.infoPills} {...{ timeToFinish, difficulty }} lastUpdated={lastUpdated}/>
        )}

        <SignInDialog
          redirectUrl={`/new-courses/${slug}/${chapter?.slug}/${section?.slug}`}
          allowSkip
        >
          {isLoading ? (
            <Skeleton style={{ width: "120px", height: "20px" }} />
          ) : (
            <Button onClick={() => trackClick(isStarted, chapter, section)}>
              {isStarted && !isCompleted ? t("continue") : isCompleted ? t("revise") : t("enroll")}
              <IoIosArrowForward />
            </Button>
          )}
        </SignInDialog>
      </section>
    </header>
  )
}

export default CourseHeader
